import { Box, Typography, Button, TextField, MenuItem, Chip, Grid, Divider, Stack } from '@mui/material';
import { Activity, AlertTriangle, User, Phone } from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import { AccordionSection } from '../common/AccordionSection';
import { Patient } from '../../types';

interface VitalFormData {
  bloodGroup: string;
  allergies: string[];
  chronicConditions: string[];
}

interface PatientMedicalInfoSectionProps {
  expanded: string | false;
  onExpandChange: (panel: string) => void;
  displayPatient: Patient;
  patient: Patient;
  editingVitals: boolean;
  setEditingVitals: (v: boolean) => void;
  vitalsSaving: boolean;
  vitalFormData: VitalFormData;
  setVitalFormData: React.Dispatch<React.SetStateAction<VitalFormData>>;
  allergyInput: string;
  setAllergyInput: (v: string) => void;
  conditionInput: string;
  setConditionInput: (v: string) => void;
  handleSaveVitals: () => void;
  handleAddAllergy: () => void;
  handleRemoveAllergy: (index: number) => void;
  handleAddCondition: () => void;
  handleRemoveCondition: (index: number) => void;
  canEditVitals: boolean | string | null | undefined;
  isDark: boolean;
}

function ChipList({
  items,
  color,
  onDelete,
}: {
  items: string[];
  color: 'error' | 'info';
  onDelete?: (index: number) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {items.map((item, index) => (
        <Chip
          key={index}
          label={item}
          size="small"
          color={color}
          variant={onDelete ? undefined : 'outlined'}
          onDelete={onDelete ? () => onDelete(index) : undefined}
        />
      ))}
    </Box>
  );
}

function AddChipRow({
  value,
  onChange,
  onAdd,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onAdd: () => void;
  placeholder: string;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onAdd(); }
        }}
      />
      <Button variant="outlined" size="small" onClick={onAdd} sx={{ whiteSpace: 'nowrap' }}>
        Add
      </Button>
    </Box>
  );
}

export function PatientMedicalInfoSection({
  expanded,
  onExpandChange,
  displayPatient,
  patient,
  editingVitals,
  setEditingVitals,
  vitalsSaving,
  vitalFormData,
  setVitalFormData,
  allergyInput,
  setAllergyInput,
  conditionInput,
  setConditionInput,
  handleSaveVitals,
  handleAddAllergy,
  handleRemoveAllergy,
  handleAddCondition,
  handleRemoveCondition,
  canEditVitals,
  isDark,
}: PatientMedicalInfoSectionProps) {
  const theme = useTheme();

  return (
    <AccordionSection
      panelId="medical-info"
      expanded={expanded}
      onExpandChange={onExpandChange}
      icon={<Activity size={20} color={theme.palette.primary.main} />}
      title="Medical Information"
      headerActions={
        canEditVitals && !editingVitals ? (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); setEditingVitals(true); }}
          >
            Edit
          </Button>
        ) : undefined
      }
      detailsSx={{ bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)' }}
    >
      {editingVitals ? (
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Blood Group</Typography>
            <TextField
              select fullWidth size="small"
              value={vitalFormData.bloodGroup}
              onChange={(e) => setVitalFormData(prev => ({ ...prev, bloodGroup: e.target.value }))}
            >
              <MenuItem value="">Select Blood Group</MenuItem>
              {['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'].map(bg => (
                <MenuItem key={bg} value={bg}>{bg}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Allergies</Typography>
            <Stack spacing={1}>
              <AddChipRow value={allergyInput} onChange={setAllergyInput} onAdd={handleAddAllergy} placeholder="Enter allergy..." />
              <ChipList items={vitalFormData.allergies} color="error" onDelete={handleRemoveAllergy} />
            </Stack>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Chronic Conditions</Typography>
            <Stack spacing={1}>
              <AddChipRow value={conditionInput} onChange={setConditionInput} onAdd={handleAddCondition} placeholder="Enter chronic condition..." />
              <ChipList items={vitalFormData.chronicConditions} color="info" onDelete={handleRemoveCondition} />
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}>
            <Button variant="outlined" onClick={() => setEditingVitals(false)} disabled={vitalsSaving}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveVitals} disabled={vitalsSaving}>
              {vitalsSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Activity size={18} color="#d32f2f" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Blood Group:</Typography>
              {displayPatient.bloodGroup
                ? <Chip label={displayPatient.bloodGroup} size="small" color="error" variant="outlined" />
                : <Typography variant="body2" color="text.secondary">Not specified</Typography>}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <AlertTriangle size={18} color="#ed6c02" />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Allergies:</Typography>
                {displayPatient.allergies && displayPatient.allergies.length > 0
                  ? <ChipList items={displayPatient.allergies as string[]} color="error" />
                  : <Typography variant="body2" color="text.secondary">No known allergies</Typography>}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Activity size={18} color="#0288d1" />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Chronic Conditions:</Typography>
                {displayPatient.chronicConditions && displayPatient.chronicConditions.length > 0
                  ? <ChipList items={displayPatient.chronicConditions as string[]} color="info" />
                  : <Typography variant="body2" color="text.secondary">No chronic conditions reported</Typography>}
              </Box>
            </Box>
          </Grid>

          {patient.emergencyContact && (patient.emergencyContact.name || patient.emergencyContact.phone) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <User size={18} color="#2e7d32" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Emergency Contact:</Typography>
                  {patient.emergencyContact.name && (
                    <Typography variant="body2"><strong>Name:</strong> {patient.emergencyContact.name}</Typography>
                  )}
                  {patient.emergencyContact.phone && (
                    <Typography variant="body2">
                      <Phone size={14} style={{ display: 'inline', marginRight: 4 }} />
                      {patient.emergencyContact.phone}
                    </Typography>
                  )}
                  {patient.emergencyContact.relation && (
                    <Typography variant="body2" color="text.secondary">({patient.emergencyContact.relation})</Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </AccordionSection>
  );
}
